import {
  LitElement,
  html,
  css,
  unsafeHTML,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * Skio Plan Picker — Variants Widget
 *
 * A template-scoped fork of skio-plan-picker-component.js, registered under
 * its own tag (skio-variants-plan-picker) so it never collides with the
 * shared <skio-plan-picker> element used elsewhere in the theme. Ported from
 * the OwnKind UAE store and generalised so the same asset can be dropped
 * into any of the consolidated product templates (electrolytes, creatine,
 * clear whey protein) purely through the `options` attribute — no
 * family-specific defaults are baked in here.
 *
 * Adds a "flavour aware" layer on top of the base plan picker: it listens
 * for variant changes made by product-flavour-selector.js (or any script
 * that dispatches a `variantChanged` CustomEvent / fires `change` on the
 * form's `[name="id"]` input) and re-renders selling plans, pricing and the
 * "Includes X packs" line for the newly selected variant.
 *
 * @docs https://integrate.skio.com/
 */
export class SkioVariantsPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    selectedVariant: { type: Object },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },
    debug: { type: Boolean },
  };

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 480px;
        font-family: inherit;
      }

      .skio-variants-subtitle {
        color: var(--skio-variants-subtext-color, #767676);
        font-size: 13px;
        margin: 0 0 10px;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .skio-cards {
        display: flex;
        flex-direction: column;
        gap: 14px;
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        padding: 0;
        margin: 0;
        border: 0;
      }

      .skio-card {
        position: relative;
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        border-radius: 3px;
        border: 1px solid var(--skio-variants-border-color, #e2e2e2);
        background: #fff;
        overflow: hidden;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
      }

      .skio-card--selected {
        border-color: var(--skio-variants-selected-border-color, #111);
        box-shadow: 0 0 0 1px var(--skio-variants-selected-border-color, #111);
      }

      .skio-card__input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .skio-card__input:focus-visible ~ .skio-card__body {
        outline: 2px solid var(--skio-variants-accent-color, #157f35);
        outline-offset: -2px;
      }

      .skio-card__banner {
        display: block;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        text-align: center;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        padding: 11px 16px;
      }

      .skio-card__body {
        display: block;
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        padding: 16px 18px;
        cursor: pointer;
      }

      .skio-card__top {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .skio-radio {
        flex-shrink: 0;
        margin-top: 2px;
        color: #b9b9b9;
      }

      .skio-card--selected .skio-radio {
        color: var(--skio-variants-accent-color, #157f35);
      }

      .skio-radio__dot {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.2s ease, opacity 0.2s ease;
      }

      .skio-card--selected .skio-radio__dot {
        opacity: 1;
        transform: scale(1);
      }

      .skio-card__title-wrap {
        flex: 1;
        min-width: 0;
      }

      .skio-card__title-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }

      .skio-card__title {
        font-size: 17px;
        font-weight: 700;
        color: #111;
      }

      .skio-badge {
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.2px;
        text-transform: uppercase;
        color: #fff;
        background: var(--skio-variants-accent-color, #157f35);
        border-radius: 999px;
        padding: 4px 10px;
      }

      .skio-card__price {
        margin-left: auto;
        flex-shrink: 0;
        text-align: right;
      }

      .skio-card__price-compare {
        display: block;
        font-size: 13px;
        color: #9c9c9c;
        text-decoration: line-through;
      }

      .skio-card__price-current {
        font-size: 20px;
        font-weight: 800;
        color: #111;
        line-height: 1.3;
      }

      .skio-card__price-suffix {
        font-size: 12px;
        color: #8a8a8a;
      }

      .skio-card__packs-line {
        margin-top: 4px;
        font-size: 12.5px;
        font-weight: 500;
        color: var(--skio-variants-subtext-color, #767676);
      }

      .skio-card__subtitle {
        margin: 4px 0 0;
        font-size: 12.5px;
        font-weight: 500;
        color: var(--skio-variants-subtext-color, #767676);
        white-space: pre-line;
        overflow-wrap: anywhere;
      }

      .skio-card__divider {
        border-top: 1px solid #ececec;
        margin: 16px 0;
      }

      .skio-checklist {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 9px;
      }

      .skio-checklist li {
        display: flex;
        align-items: flex-start;
        gap: 9px;
        font-size: 13.5px;
        font-weight: 600;
        color: #161616;
        line-height: 1.35;
      }

      .skio-checklist svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: var(--skio-variants-accent-color, #157f35);
      }

      .skio-gifts {
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        margin-top: 18px;
      }

      .skio-gifts__heading {
        text-align: right;
        font-size: 14px;
        font-weight: 800;
        color: #111;
        margin: 0 0 10px;
      }

      .skio-gifts__grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        padding: 10px;
        border-radius: var(--buttons-radius, 10px);
        background: linear-gradient(135deg, #f8a6b5 0%, #EA7090 100%);
      }
        
      .skio-gift {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        box-sizing: border-box;
        min-width: 0;
        background: #fdf6ee;
        border-radius: 10px;
        padding: 5px 6px 5px;
        text-align: center;
      }

      .skio-gift__art {
        width: 100%;
        height: 76px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .skio-gift__art svg,
      .skio-gift__art img {
        max-height: 100%;
        max-width: 100%;
      }

      .skio-gift__caption {
        box-sizing: border-box;
        max-width: 100%;
        background: var(--skio-variants-accent-color, #157f35);
        color: #fff;
        font-size: 9.5px;
        font-weight: 700;
        padding: 4px 8px;
        border-radius: 999px;
        white-space: nowrap;
      }

      .skio-onetime-price {
        font-size: 18px;
        font-weight: 700;
        color: #111;
      }

      .skio-setup-mode {
        position: relative;
        color: #000;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(
            -55deg,
            #000,
            #000 20px,
            #ffb101 20px,
            #ffb101 40px
          )
          10;
      }

      @media (max-width: 480px) {
        .skio-card__body {
          padding: 14px;
        }

        .skio-gifts__grid {
          padding: 12px;
          gap: 8px;
        }

        .skio-gift__ribbon,
        .skio-gift__caption {
          font-size: 8.5px;
          overflow-wrap: anywhere;
          white-space: normal;
        }
      }
    `,
  ];

  static DEFAULT_CHECKLIST = [
    "Save [discount] on every order",
    "Free shipping on subscription orders",
    "Skip, pause or cancel anytime",
  ];

  constructor() {
    super();

    this.debug = this.debug || window?.Shopify?.designMode;
    this.userExplicitlyChoseOnetime = false;

    this.rules = [];
    this.rule = null;
    this.quantity = 1;
  }

  async connectedCallback() {
    super.connectedCallback();

    this.bindFormEvents();
    this.bindCartEvents();

    this.rules = await this.fetchRules();
    this.getRule();
  }

  // SECTION: Small icon templates
  radioTemplate() {
    return html`
      <span class="skio-radio">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="11" stroke="currentColor" stroke-width="1.5" />
          <circle class="skio-radio__dot" cx="12" cy="12" r="6" fill="currentColor" />
        </svg>
      </span>
    `;
  }

  checkIcon() {
    return html`
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="currentColor" fill-opacity="0.12" />
        <path
          d="M7.5 12.5l3 3 6-6.5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  giftIconBox() {
    return html`
      <svg width="64" height="64" viewBox="0 0 44 44" fill="none">
        <rect x="7" y="18" width="30" height="18" rx="2" fill="currentColor" opacity="0.85" />
        <rect x="19" y="18" width="6" height="18" fill="currentColor" opacity="0.6" />
        <path
          d="M22 8c-4 0-6 3-6 5.5s2 4.5 6 4.5 6-2 6-4.5S26 8 22 8z"
          fill="currentColor"
          opacity="0.4"
        />
      </svg>
    `;
  }

  giftIconShipping() {
    return html`
      <svg width="64" height="64" viewBox="0 0 44 44" fill="none">
        <rect x="5" y="16" width="27" height="20" rx="2" fill="currentColor" opacity="0.85" />
        <rect x="16.5" y="16" width="6" height="20" fill="currentColor" opacity="0.55" />
        <path
          d="M19.5 6c-4 0-6 3-6 5.5s2 4.5 6 4.5 6-2 6-4.5-2-5.5-6-5.5z"
          fill="currentColor"
          opacity="0.35"
        />
        <circle cx="33" cy="30" r="10" fill="#fff" stroke="currentColor" stroke-width="2" />
        <path
          d="M28.5 30.2l3 3 6-6.7"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  giftIconVariety() {
    return html`
      <svg width="64" height="64" viewBox="0 0 44 44" fill="none">
        <rect x="4" y="12" width="9" height="24" rx="3" fill="currentColor" opacity="0.5" />
        <rect x="15" y="6" width="9" height="30" rx="3" fill="currentColor" opacity="0.85" />
        <rect x="26" y="12" width="9" height="24" rx="3" fill="currentColor" opacity="0.65" />
        <rect x="15" y="6" width="9" height="7" rx="2.5" fill="currentColor" opacity="0.4" />
      </svg>
    `;
  }

  giftIconMystery() {
    return html`
      <svg width="64" height="64" viewBox="0 0 44 44" fill="none">
        <rect x="6" y="14" width="32" height="22" rx="3" fill="currentColor" opacity="0.85" />
        <path d="M22 4l4.5 6h-9L22 4z" fill="currentColor" opacity="0.4" />
        <text x="22" y="30.5" text-anchor="middle" font-size="15" font-weight="700" fill="#fff">
          ?
        </text>
      </svg>
    `;
  }
  // !SECTION: Small icon templates

  giftArt(gift) {
    if (gift.image) {
      return html`<img src="${gift.image}" alt="${gift.caption ?? ""}" width="64" height="64" style="object-fit:contain;max-width:100%;height:auto;" />`;
    }

    switch (gift.icon) {
      case "shipping":
        return this.giftIconShipping();
      case "variety":
        return this.giftIconVariety();
      case "mystery":
        return this.giftIconMystery();
      default:
        return this.giftIconBox();
    }
  }

  giftGrid() {
    const gifts = this.options?.gift_items ?? [];
    if (!gifts.length) return "";

    return html`
      <div class="skio-gifts">
        <p class="skio-gifts__heading">${this.options?.gifts_heading ?? "Free Gifts"}</p>
        <div class="skio-gifts__grid">
          ${gifts.map(
            (gift) => html`
              <div class="skio-gift">
                <span class="skio-gift__ribbon ${gift.ribbon ? "" : "skio-gift__ribbon--hidden"}">${gift.ribbon || " "}</span>
                <span class="skio-gift__art">${this.giftArt(gift)}</span>
                <span class="skio-gift__caption">${gift.caption}</span>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }

  checklist(plan) {
    const items = this.options?.checklist_items ?? SkioVariantsPlanPicker.DEFAULT_CHECKLIST;
    const discountText = this.discount(plan).percent + "%";

    return html`
      <ul class="skio-checklist">
        ${items.map(
          (item) => html`
            <li>
              ${this.checkIcon()}
              <span>${unsafeHTML(item.replaceAll("[discount]", discountText))}</span>
            </li>
          `
        )}
      </ul>
    `;
  }

  // Flattens every selling plan across every available group into a single
  // ordered list, so each cadence (e.g. 30-day, 90-day) renders as its own
  // full card instead of being nested inside one group behind a selector —
  // Skio groups can bundle multiple cadences into a single selling plan
  // group, but the design calls for one card per cadence.
  get flattenedPlans() {
    return (this.availableSellingPlanGroups ?? []).flatMap((group) =>
      this.getAvailableSellingPlans(group).map((plan) => ({ plan, group }))
    );
  }

  badgeFor(index, plan) {
    const template = this.options?.badge_templates?.[index] ?? "Save [discount]";
    const discount = this.discount(plan);
    if (!discount.percent) return "";
    return template.replaceAll("[discount]", discount.percent + "%");
  }

  bannerFor(index, plan) {
    const banner = this.options?.card_banners?.[index];
    if (!banner?.text) return "";
    const discount = this.discount(plan).percent + "%";
    return {
      text: banner.text.replaceAll("[discount]", discount),
      bg: banner.bg || "var(--skio-variants-accent-color, #157f35)",
    };
  }

  // Resolves how many packs/units ship under a given selling plan, by
  // matching the plan's name against `quantity_by_plan_match` (an ordered
  // list of { match, quantity } pairs). Falls back to 1 so a plan with no
  // explicit mapping still adds a single unit rather than nothing.
  quantityForPlan(plan) {
    const mappings = this.options?.quantity_by_plan_match;
    if (!Array.isArray(mappings) || !plan?.name) return 1;

    const planName = plan.name.toLowerCase();
    const match = mappings.find((entry) =>
      planName.includes(String(entry?.match ?? "").toLowerCase())
    );

    return match ? Math.max(1, Number(match.quantity) || 1) : 1;
  }

  packsLine(plan) {
    if (!this.options?.show_packs_line) return "";

    const quantity = this.quantityForPlan(plan);
    const unitPrice = this.price(plan, false);
    const total = this.money(unitPrice * quantity);
    const template =
      this.options?.packs_line_template ?? "Includes [quantity] [pack_word]. [total] total";

    const text = template
      .replaceAll("[quantity]", String(quantity))
      .replaceAll("[pack_word]", quantity === 1 ? "pack" : "packs")
      .replaceAll("[total]", total);

    return html`<p class="skio-card__packs-line">${text}</p>`;
  }

  planCard(entry, index) {
    const { plan } = entry;
    const selected = this.selectedSellingPlan?.id === plan.id;
    const banner = this.bannerFor(index, plan);
    const badge = this.badgeFor(index, plan);
    const discount = this.discount(plan);
    const comparisonPrice =
      this.selectedVariant?.compare_at_price > this.selectedVariant?.price
        ? this.selectedVariant.compare_at_price
        : this.selectedVariant?.price;
    const showComparisonPrice =
      discount.amount > 0 && comparisonPrice > this.price(plan, false);

const packQuantity = this.quantityForPlan(plan);
const totalPrice = this.money(this.price(plan, false) * packQuantity);
const totalComparePrice = this.money(comparisonPrice * packQuantity);

    const showGiftBadge = this.options?.free_gift_badge_index === index;
    // free_gift_badge_index doubles as the gift grid's scope: when set, only
    // that one plan card shows gifts (e.g. 30-day only, not 90-day). Leaving
    // it unset/-1 falls back to showing gifts on every subscription plan.
    const giftPlanIndex = this.options?.free_gift_badge_index;
    const showGiftGrid =
      this.options?.show_gifts_for_subscriptions !== false &&
      (giftPlanIndex == null || giftPlanIndex === -1 || giftPlanIndex === index);

    return html`
      <div class="skio-card ${selected ? "skio-card--selected" : ""}">
        ${banner
          ? html`<div class="skio-card__banner" style="background:${banner.bg}">${banner.text}</div>`
          : ""}
        <input
          type="radio"
          id="plan-${index}-${this.key}"
          class="skio-card__input"
          name="skio-group-${this.key}"
          value="${plan.id}"
          ?checked=${selected}
          @change=${() => this.selectSellingPlan(plan.id)}
        />
        <label class="skio-card__body" for="plan-${index}-${this.key}">
          <div class="skio-card__top">
            ${this.radioTemplate()}
            <div class="skio-card__title-wrap">
              <div class="skio-card__title-row">
                <span class="skio-card__title">${plan.name}</span>
                ${badge ? html`<span class="skio-badge">${badge}</span>` : ""}
                ${showGiftBadge && this.options?.free_gift_badge_text
                  ? html`<span class="skio-badge">${this.options.free_gift_badge_text}</span>`
                  : ""}
              </div>
              ${this.options?.plan_subtitles?.[index]
                ? html`<p class="skio-card__subtitle">${this.options.plan_subtitles[index]}</p>`
                : this.packsLine(plan)}
            </div>
            
<div class="skio-card__price">
  ${showComparisonPrice
    ? html`<span class="skio-card__price-compare">${totalComparePrice}</span>`
    : ""}

  <div class="skio-card__price-current">
    ${totalPrice}
  </div>

  <div class="skio-card__price-suffix">
    total today
  </div>

  ${packQuantity > 1
    ? html`
        <div class="skio-card__price-suffix">
          ${this.price(plan)} per pack
        </div>
      `
    : ""}
</div>

          </div>

          ${selected
            ? html`
                <div class="skio-card__divider"></div>
                ${this.checklist(plan)}
                ${showGiftGrid ? this.giftGrid() : ""}
              `
            : ""}
        </label>
      </div>
    `;
  }

  onetimeCard() {
    const selected = this.selectedSellingPlanGroup == null;

    return html`
      <div class="skio-card ${selected ? "skio-card--selected" : ""}">
        <input
          type="radio"
          id="onetime-${this.key}"
          class="skio-card__input"
          name="skio-group-${this.key}"
          value="onetime"
          ?checked=${selected}
          @change=${() => this.selectSellingPlanGroup(null, true)}
        />
        <label class="skio-card__body" for="onetime-${this.key}">
          <div class="skio-card__top">
            ${this.radioTemplate()}
            <div class="skio-card__title-wrap">
              <span class="skio-card__title">${this.options?.onetime_title ?? "One-time purchase"}</span>
            </div>
            <div class="skio-card__price">
              <div class="skio-onetime-price">${this.money(this.selectedVariant.price)}</div>
              ${this.options?.price_suffix_text
                ? html`<div class="skio-card__price-suffix">${this.options.price_suffix_text}</div>`
                : ""}
            </div>
          </div>
        </label>
      </div>
    `;
  }

  setupMode() {
    return html`
      <div class="skio-setup-mode">
        Skio Variants Plan Picker
        <small>This will not appear on the live site. Key: ${this.key}</small>
        <small>Product: ${this.product ? "found" : "missing"} · Variant: ${
      this.selectedVariant ? "found" : "missing"
    } · Form: ${this.form ? "found" : "missing"}</small>
      </div>
    `;
  }

  render() {
    if (!this.viable()) return this.debug ? this.setupMode() : "";

    if (
      this.selectedVariant.selling_plan_allocations.length === 0 &&
      !this.options?.show_without_subscription
    ) {
      return this.debug ? this.setupMode() : "";
    }

    return html`
      ${this.debug ? this.setupMode() : ""}
      ${this.options?.subtitle
        ? html`<p class="skio-variants-subtitle">${this.options.subtitle}</p>`
        : ""}
      <fieldset class="skio-cards" role="radiogroup" aria-labelledby="skio-picker-legend-${this.key}">
        <legend id="skio-picker-legend-${this.key}" class="sr-only">
          ${this.options?.legend_content ?? "Select a purchase option"}
        </legend>
        ${this.flattenedPlans.map((entry, index) => this.planCard(entry, index))}
        ${!this.product.requires_selling_plan ? this.onetimeCard() : ""}
      </fieldset>
    `;
  }

  // SECTION: Data lifecycle (ported from skio-plan-picker-component.js)
  async fetchRules() {
    if (sessionStorage.getItem("skio-discount-rules")) {
      return JSON.parse(sessionStorage.getItem("skio-discount-rules"));
    }

    const rules = await fetch(
      `https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`
    )
      .then((response) => response.json())
      .then((response) =>
        response.rules
          .filter((rule) => rule.type !== "surpriseDelight")
          .sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
      );

    sessionStorage.setItem("skio-discount-rules", JSON.stringify(rules));
    return rules;
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some((gid) =>
      gid.includes(this.selectedVariant.id)
    )
      ? this.selectedPurchaseQuantity()
      : 0;

    if (!this.cart?.items) return eligibleQuantity;

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(
            (gid) =>
              gid.includes(item.id) &&
              item.selling_plan_allocation &&
              gid.includes(this.selectedVariant.id)
          )
            ? aggregate + item.quantity
            : aggregate,
        0
      )
    );

    return cartItems + eligibleQuantity;
  }

  getRule() {
    let largestMinQuantity = { value: -1, index: -1 };

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount;
          largestMinQuantity.index = index;
        }
      }
    });

    this.rule =
      largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null;
  }

  bindCartEvents() {
    document.addEventListener("CartUpdated", (event) => {
      this.cart = event.detail;
    });
  }

  // The cart form is owned by the buy_buttons block, which can render before
  // or after this widget depending on block order — so form lookup can't rely
  // on DOM ancestry (this.closest) and uses getElementById (not querySelector
  // with a manually-interpolated id, which throws on ids CSS can't parse) plus
  // a document-wide fallback and a short-lived observer for render-order races.
  resolveForm() {
    const byId = this.options?.form_id && document.getElementById(this.options.form_id);
    return byId || this.closest('form[action*="/cart/add"]') || document.querySelector('form[data-type="add-to-cart-form"]');
  }

  bindFormEvents() {
    this.form = this.resolveForm();

    if (!this.form) {
      this.formObserver = new MutationObserver(() => {
        this.form = this.resolveForm();
        if (this.form) {
          this.formObserver.disconnect();
          this.formObserver = null;
          this.attachFormListeners();
          this.requestUpdate();
        }
      });
      this.formObserver.observe(document.body, { childList: true, subtree: true });
      return;
    }

    this.attachFormListeners();
  }

  attachFormListeners() {
    this.variantInput = this.form.querySelector('[name="id"]');

    // Re-apply plan quantity in the capture phase so it is present before the
    // product form builds FormData, even if another script changed the input.
    this.form.addEventListener("submit", () => this.updateForm(), { capture: true });

    if (this.variantInput) {
      this.variantInput.addEventListener("change", (e) => {
        this.selectedVariant = this.product.variants.find(
          (variant) => variant.id == e.target.value
        );
      });
    }

    this.quantityInput = this.form.querySelector('[name="quantity"]');
    if (this.quantityInput) {
      this.quantity = Number(this.quantityInput.value);
      this.quantityInput.addEventListener("input", () => {
        this.quantity = Number(this.quantityInput.value);
      });
    }

    document.addEventListener("variantChanged", (e) => {
      const variant = this.product.variants.find((x) => x.id == e.detail.variantId);
      if (variant) this.selectedVariant = variant;
      this.requestUpdate();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.formObserver?.disconnect();
  }

  viable() {
    const errors = [];

    if (!this.querySelector("input[name=selling_plan]")) {
      errors.push("Missing selling plan input");
    }
    if (!this.product) errors.push("No product found.");
    if (!this.selectedVariant) errors.push("No variant found.");
    if (!this.form) errors.push("No cart form found.");
    if (!this.variantInput) errors.push("No variant input found.");

    if (errors.length === 0) return true;

    console.error(`[skio variants plan picker][${this.key}]`, errors);
    return false;
  }

  getAvailableSellingPlans(group) {
    return group.selling_plans.filter((selling_plan) =>
      this.selectedVariant.selling_plan_allocations.some(
        (allocation) => allocation.selling_plan_id === selling_plan.id
      )
    );
  }

  selectSellingPlanGroup(group, userExplicit = false) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    this.userExplicitlyChoseOnetime = group === null ? userExplicit : false;
  }

  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find((group) =>
      group.selling_plans.some((plan) => plan.id == selling_plan_id)
    );
    const selectedSellingPlan = selectedGroup.selling_plans.find(
      (plan) => plan.id == selling_plan_id
    );

    selectedGroup.selected_selling_plan = selectedSellingPlan;
    this.selectedSellingPlanGroup = selectedGroup;
    this.selectedSellingPlan = selectedSellingPlan;
    this.userExplicitlyChoseOnetime = false;
  }

  updateExternalPrice() {
    if (!this.options?.external_price_selector) return;

    document.querySelectorAll(this.options.external_price_selector).forEach((el) => {
      el.innerHTML = this.selectedSellingPlan
        ? this.price(this.selectedSellingPlan)
        : this.money(this.selectedVariant.price);
    });
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0, absolute: 0 };
    return this.getDiscountFromPriceAdjustment(
      selling_plan,
      selling_plan.price_adjustments[0]
    );
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = { percent: 0, amount: 0, absolute: 0 };
    const price = this.selectedVariant.price;
    const compareAtPrice =
      this.selectedVariant.compare_at_price > price
        ? this.selectedVariant.compare_at_price
        : price;

    switch (price_adjustment?.value_type) {
      case "percentage":
        discount.percent = price_adjustment.value;
        discount.absolute = Math.round((price * price_adjustment.value) / 100);
        discount.amount = discount.absolute;
        break;
      case "fixed_amount":
        discount.percent = Math.round((price_adjustment.value / price) * 100);
        discount.absolute = price_adjustment.value;
        discount.amount = price_adjustment.value;
        break;
      case "price":
        discount.percent = Math.round(
          ((compareAtPrice - price_adjustment.value) / compareAtPrice) * 100
        );
        discount.absolute = compareAtPrice - price_adjustment.value;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/;

    function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
      if (isNaN(number) || number == null) return "0";

      const numString = (number / 100).toFixed(precision);
      const parts = numString.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const cents = parts[1] ? decimal + parts[1] : "";
      return dollars + cents;
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case "amount":
          return formatWithDelimiters(cents, 2);
        case "amount_no_decimals":
          return formatWithDelimiters(cents, 0);
        case "amount_with_comma_separator":
          return formatWithDelimiters(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return formatWithDelimiters(cents, 0, ".", ",");
        case "amount_with_apostrophe_separator":
          return formatWithDelimiters(cents, 2, "'", ".");
        case "amount_no_decimals_with_space_separator":
          return formatWithDelimiters(cents, 0, " ");
        case "amount_with_space_separator":
          return formatWithDelimiters(cents, 2, " ", ",");
        case "amount_with_period_and_space_separator":
          return formatWithDelimiters(cents, 2, " ", ".");
        default:
          return match;
      }
    });
  }

  price(selling_plan, formatted = true) {
    const discountAmount = this.discount(selling_plan).amount;
    const ruleAmount = this.rule
      ? this.selectedVariant.price * (this.rule.discountAmount / 100)
      : 0;
    const finalPrice = this.selectedVariant.price - discountAmount - ruleAmount;

    return formatted ? this.money(finalPrice) : finalPrice;
  }

  selectedPurchaseQuantity() {
    return this.quantityForPlan(this.selectedSellingPlan);
  }

  updateForm() {
    const $sellingPlan = this.querySelector("input[name=selling_plan]");

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : "";
      $sellingPlan.dispatchEvent(new Event("change"));
    }

    if (this.quantityInput) {
      this.quantityInput.value = String(this.selectedPurchaseQuantity());
    }
  }

  updateURLParams() {
    if (this.disableUrl) return;

    const url = new URL(window.location.href);
    if (this.selectedSellingPlan) {
      url.searchParams.set("selling_plan", this.selectedSellingPlan.id);
    } else {
      url.searchParams.delete("selling_plan");
    }
    window.history.replaceState({}, "", url.href);
  }

  updated(changed) {
    if (!this.viable()) return;

    if (changed.has("product") && this.product) {
      this.key = this.key ? this.key : this.product.id;
    }

    if (changed.has("selectedVariant") && this.selectedVariant) {
      this.availableSellingPlanGroups = this.product.selling_plan_groups.filter(
        (group) =>
          group.app_id === "SKIO" &&
          !group.name.toLowerCase().includes("hidden-") &&
          !group.name.toLowerCase().includes("dynamic box subscription") &&
          group.selling_plans.some((plan) =>
            this.selectedVariant.selling_plan_allocations.some(
              (allocation) => allocation.selling_plan_id === plan.id
            )
          )
      );

      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href);
        const urlSelectedSellingPlanId = url.searchParams.get("selling_plan");

        this.availableSellingPlanGroups.forEach((group) => {
          const availablePlans = this.getAvailableSellingPlans(group);
          const urlSelectedPlan = availablePlans.find((plan) => plan.id == urlSelectedSellingPlanId);
          const nameSelectedPlan = availablePlans.find(
            (plan) => plan.name === this.selectedSellingPlan?.name
          );
          const defaultPlan =
            this.options?.default_subscription?.trim()
              ? availablePlans.find((plan) =>
                  plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase())
                )
              : null;

          group.selected_selling_plan = urlSelectedPlan || nameSelectedPlan || defaultPlan || availablePlans[0];
          group.selected = !!(urlSelectedPlan || nameSelectedPlan || defaultPlan);
        });

        if (
          (!this.userExplicitlyChoseOnetime && this.options?.start_onetime == false) ||
          this.product.requires_selling_plan ||
          urlSelectedSellingPlanId ||
          this.selectedSellingPlan
        ) {
          const selectedGroup =
            this.availableSellingPlanGroups.find((group) => group.selected) ||
            this.availableSellingPlanGroups[0];
          this.selectSellingPlan(selectedGroup.selected_selling_plan.id);
        } else {
          this.selectSellingPlanGroup(null);
        }
      } else {
        this.selectSellingPlanGroup(null);
      }
    }

    if (changed.has("selectedSellingPlan")) {
      this.dispatchEvent(
        new CustomEvent("skio::update-selling-plan", {
          bubbles: true,
          composed: true,
          detail: {
            variant: this.selectedVariant,
            sellingPlan: this.selectedSellingPlan,
            key: this.key,
          },
        })
      );

      this.dispatchEvent(
        new CustomEvent("change", { detail: { value: this.selectedSellingPlan?.id } })
      );
    }

    this.updateForm();
    this.updateExternalPrice();
    this.updateURLParams();

    if (changed.has("quantity") || changed.has("cart")) {
      this.getRule();
    }
  }
  // !SECTION: Data lifecycle
}

if (!customElements.get("skio-variants-plan-picker")) {
  customElements.define("skio-variants-plan-picker", SkioVariantsPlanPicker);
}
